import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useMst } from '../../model/Root';
import { EventLog } from '../../types';
import { fetchEvent, fetchEventTrace } from '../../services/eventsApi';
import { getOutcomeColor } from '../../utils/events';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const formatTimestamp = (event: EventLog | null, fallbackLabel: string) => {
  if (!event) return fallbackLabel;
  const timestamp = event.occurredAt || event.timestamp || event.createdAt;
  if (!timestamp) return fallbackLabel;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }
  return parsed.toLocaleString('fi-FI', {
    timeZone: 'Europe/Helsinki',
    dateStyle: 'short',
    timeStyle: 'medium',
  });
};

const EventDetailScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId, requestId: requestIdParam } = useParams();
  const {
    errorStore: { setError },
  } = useMst();

  const [event, setEvent] = useState<EventLog | null>(null);
  const [trace, setTrace] = useState<EventLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const detail = await fetchEvent(eventId);
      setEvent(detail);
    } catch (err) {
      console.error(err);
      setError('general_error_message');
    } finally {
      setLoading(false);
    }
  }, [eventId, setError]);

  const loadTrace = useCallback(
    async (requestId: string) => {
      setTraceLoading(true);
      try {
        const response = await fetchEventTrace(requestId);
        const traceEvents = response.data ?? [];
        setTrace(traceEvents);
        setEvent((prev) => {
          if (traceEvents.length === 0) return prev;
          const first = traceEvents[0];
          if (!prev) return first;
          // If the current event is for a different request, replace it with the new trace head.
          const prevRequestMatch = prev.requestId === requestId || prev.traceId === requestId;
          return prevRequestMatch ? prev : first;
        });
      } catch (err) {
        console.error(err);
        setError('general_error_message');
      } finally {
        setTraceLoading(false);
      }
    },
    [setError]
  );

  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId, loadEvent]);

  useEffect(() => {
    const requestId = requestIdParam || event?.requestId || event?.traceId;
    if (requestId) {
      loadTrace(requestId);
    } else {
      setTrace([]);
    }
  }, [event?.requestId, event?.traceId, loadTrace, requestIdParam]);

  const timestampLabel = formatTimestamp(event, t('events.no_timestamp'));
  const requestIdLabel = useMemo(
    () => requestIdParam || event?.requestId || event?.traceId || '-',
    [event?.requestId, event?.traceId, requestIdParam]
  );

  return (
    <Layout title={t('events.detail_title')}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button onClick={() => navigate('/events')}>{t('events.back_to_list')}</Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CircularProgress size={20} />
          <Typography>{t('events.loading')}</Typography>
        </Box>
      ) : (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('events.summary')}
          </Typography>
          {event ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.timestamp')}
                </Typography>
                <Typography variant="body1">{timestampLabel}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.request_id')}
                </Typography>
                <Typography variant="body1">{requestIdLabel}</Typography>
              </Grid>
              {event.traceId && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('events.trace_id')}
                  </Typography>
                  <Typography variant="body1">{event.traceId}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.user_id')}
                </Typography>
                <Typography variant="body1">{event.userId ?? '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.event')}
                </Typography>
                <Typography variant="body1">{event.event}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.category')}
                </Typography>
                <Typography variant="body1">{event.category}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.outcome')}
                </Typography>
                <Chip
                  size="small"
                  label={t(`events.outcome_${event.outcome}`, { defaultValue: event.outcome })}
                  color={getOutcomeColor(event.outcome)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.reason_code')}
                </Typography>
                <Typography variant="body1">{event.reasonCode ?? '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('events.route')}
                </Typography>
                <Typography variant="body1">
                  {event.method ? `${event.method} ` : ''}
                  {event.route ?? '-'}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('events.no_results')}
            </Typography>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('events.trace')}</Typography>
          {traceLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">{t('events.trace_loading')}</Typography>
            </Stack>
          )}
        </Box>
        {!traceLoading && trace.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('events.trace_empty')}
          </Typography>
        ) : (
          <MuiTable size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('events.table.timestamp')}</TableCell>
                <TableCell>{t('events.table.event')}</TableCell>
                <TableCell>{t('events.table.category')}</TableCell>
                <TableCell>{t('events.table.outcome')}</TableCell>
                <TableCell>{t('events.table.reason_code')}</TableCell>
                <TableCell>{t('events.table.route')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trace.map((item) => (
                <TableRow key={`${item.id}-${item.requestId ?? 'trace'}`}>
                  <TableCell>{formatTimestamp(item, t('events.no_timestamp'))}</TableCell>
                  <TableCell>{item.event}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`events.outcome_${item.outcome}`, { defaultValue: item.outcome })}
                      color={getOutcomeColor(item.outcome)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{item.reasonCode ?? '-'}</TableCell>
                  <TableCell>
                    {item.method ? `${item.method} ` : ''}
                    {item.route ?? '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        )}
      </Paper>
    </Layout>
  );
};

export default EventDetailScreen;
